/**
 * The staff catalog, /api/internal/catalog (TRA-1507): every base model
 * teams can call, with the public upstreams a model can be added to. One
 * query key; every mutation invalidates it, and the customer model
 * picker's list, so both show what the gateway now serves.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'

import { customFetch } from '@/api/client'
import { getListProxyModelsApiProxyModelsGetQueryKey } from '@/api/generated/proxy-models/proxy-models'
import type {
  CatalogImpactResponse,
  CatalogModelCreate,
  CatalogModelUpdate,
  CatalogModelView,
  CatalogProbeRequest,
  CatalogProbeResponse,
  CatalogResponse,
} from '@/api/generated/model'

export const CATALOG_QUERY_KEY = ['/api/internal/catalog'] as const

export function useCatalog(): UseQueryResult<CatalogResponse> {
  return useQuery({
    queryKey: CATALOG_QUERY_KEY,
    queryFn: () => customFetch<CatalogResponse>('/api/internal/catalog'),
    staleTime: 60_000,
  })
}

/** The user models that route to a catalog row; read before deprecating it. */
export function useCatalogImpact(
  modelId: string | null,
): UseQueryResult<CatalogImpactResponse> {
  return useQuery({
    queryKey: [...CATALOG_QUERY_KEY, modelId, 'impact'],
    queryFn: () =>
      customFetch<CatalogImpactResponse>(
        `/api/internal/catalog/${modelId ?? ''}/impact`,
      ),
    enabled: modelId !== null,
  })
}

function useCatalogMutation<TVars>(
  request: (vars: TVars) => Promise<CatalogModelView>,
): UseMutationResult<CatalogModelView, Error, TVars> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: request,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CATALOG_QUERY_KEY })
      void queryClient.invalidateQueries({
        queryKey: getListProxyModelsApiProxyModelsGetQueryKey(),
      })
    },
  })
}

export function useCreateCatalogModel(): UseMutationResult<
  CatalogModelView,
  Error,
  CatalogModelCreate
> {
  return useCatalogMutation((body: CatalogModelCreate) =>
    customFetch<CatalogModelView>('/api/internal/catalog', {
      method: 'POST',
      data: body,
    }),
  )
}

export interface CatalogModelUpdateVars {
  id: string
  body: CatalogModelUpdate
}

export function useUpdateCatalogModel(): UseMutationResult<
  CatalogModelView,
  Error,
  CatalogModelUpdateVars
> {
  return useCatalogMutation(({ id, body }: CatalogModelUpdateVars) =>
    customFetch<CatalogModelView>(`/api/internal/catalog/${id}`, {
      method: 'PUT',
      data: body,
    }),
  )
}

export interface CatalogDeprecateVars {
  id: string
  cascade: boolean
}

export function useDeprecateCatalogModel(): UseMutationResult<
  CatalogModelView,
  Error,
  CatalogDeprecateVars
> {
  return useCatalogMutation(({ id, cascade }: CatalogDeprecateVars) =>
    customFetch<CatalogModelView>(`/api/internal/catalog/${id}/deprecate`, {
      method: 'POST',
      data: { cascade },
    }),
  )
}

export function useReactivateCatalogModel(): UseMutationResult<
  CatalogModelView,
  Error,
  string
> {
  return useCatalogMutation((id: string) =>
    customFetch<CatalogModelView>(`/api/internal/catalog/${id}/reactivate`, {
      method: 'POST',
    }),
  )
}

/**
 * One sample call to a provider model through the gateway, with the
 * upstream's own key. Nothing is written, so nothing is invalidated.
 */
export function useProbeCatalogModel(): UseMutationResult<
  CatalogProbeResponse,
  Error,
  CatalogProbeRequest
> {
  return useMutation({
    mutationFn: (body: CatalogProbeRequest) =>
      customFetch<CatalogProbeResponse>('/api/internal/catalog/probe', {
        method: 'POST',
        data: body,
      }),
  })
}
